import React from "react";

/**
 * AdminSegmentedTabs — the unified pill-tab primitive used across every Admin
 * command center. Ported from the design pattern in Doctor Billing.jsx (Patient
 * Invoices / Invisique™ Invoices). See iter100.252.
 *
 * Grammar:
 *   [ Icon  Label  count-chip ]  ← rounded pill, teal-filled when active
 *   ─────────────────────────────
 *   • Container is a rounded-xl bg-card border with p-1 padding.
 *   • Active pill: bg-primary text-white shadow-sm.
 *   • Inactive pill: text-muted-foreground with hover:bg-secondary/50.
 *   • Count chip appears when a count is present (0 counts hidden by default,
 *     override with `showZero=true`).
 *
 * Props:
 *   tabs         — [{ id, label, Icon, count?, hint?, tone? }]
 *                  tone can be "danger" | "warning" (chip stays red/amber even
 *                  when tab is inactive — used for approvals-pending etc).
 *   active       — the currently-active tab id.
 *   onChange     — (id) => void.
 *   testid       — data-testid for the container.
 *   tabTestidBase — data-testid prefix per tab button (default: `${testid}-tab`).
 *   showZero     — render the count chip even when count === 0. Default false.
 */
export default function AdminSegmentedTabs({
    tabs = [],
    active,
    onChange,
    testid = "admin-segmented-tabs",
    tabTestidBase,
    showZero = false,
    className = "",
}) {
    const testidPrefix = tabTestidBase || `${testid}-tab`;
    return (
        <div
            className={`inline-flex bg-card border border-border rounded-xl p-1 gap-0.5 max-w-full overflow-x-auto ${className}`}
            role="tablist"
            data-testid={testid}
        >
            {tabs.map((t) => {
                const on = active === t.id;
                const Icon = t.Icon;
                const hasCount = t.count != null && (t.count > 0 || showZero);
                return (
                    <button
                        key={t.id}
                        type="button"
                        role="tab"
                        aria-selected={on}
                        onClick={() => onChange?.(t.id)}
                        title={t.hint || undefined}
                        data-testid={`${testidPrefix}-${t.id}`}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                            on
                                ? "bg-primary text-white shadow-sm"
                                : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                        }`}
                    >
                        {Icon ? <Icon size={14} weight={on ? "fill" : "duotone"} /> : null}
                        {t.label}
                        {hasCount && (
                            <span
                                data-testid={`${testidPrefix}-${t.id}-count`}
                                className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums ${
                                    on
                                        ? "bg-white/25 text-white"
                                        : t.tone === "danger"
                                            ? "bg-rose-100 text-rose-700"
                                            : t.tone === "warning"
                                                ? "bg-amber-100 text-amber-800"
                                                : "bg-secondary text-muted-foreground"
                                }`}
                            >
                                {t.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
}
